<template>
  <div class="watchlist-container" :class="{ dark: isDark }">
    <!-- Header -->
    <div class="watchlist-header">
      <div class="watchlist-title">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
        <span>Watchlist</span>
      </div>
      <div class="watchlist-header-actions">
        <button class="watchlist-icon-btn" @click="showAddSearch = !showAddSearch" title="Search symbol">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        </button>
        <button class="watchlist-icon-btn" @click="$emit('toggle-expand')" :title="isExpanded ? 'Switch to inline mode' : 'Switch to float mode'">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <!-- Float mode icon (expanded) - floating panel -->
            <rect v-if="isExpanded" x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <line v-if="isExpanded" x1="9" y1="9" x2="15" y2="9"/>
            <line v-if="isExpanded" x1="9" y1="15" x2="15" y2="15"/>
            <!-- Inline mode icon (collapsed) - docked window -->
            <g v-else><rect x="2" y="4" width="20" height="16" rx="2" ry="2"/>
            <line x1="2" y1="9" x2="22" y2="9"/>
            <line x1="2" y1="15" x2="22" y2="15"/></g>
          </svg>
        </button>
        <button class="watchlist-icon-btn" @click="$emit('close')" title="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Add Symbol Search Bar -->
    <transition name="wl-fade">
      <div v-if="showAddSearch" class="watchlist-search-bar">
        <div class="watchlist-search-input-wrap">
          <svg class="wl-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="watchlist-search-input"
            placeholder="Search symbol to add..."
            @input="onSearchInput"
            @keydown.enter="addFirstResult"
            @keydown.esc="closeSearch"
          />
          <button v-if="searchQuery" class="wl-clear-btn" @click="clearSearch">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Search Results Dropdown -->
        <div v-if="searchResults.length > 0" class="watchlist-search-results">
          <div
            v-for="(item, idx) in searchResults.slice(0, 10)"
            :key="idx"
            class="wl-search-result-item"
            @click="addSymbol(item)"
          >
            <div class="wl-result-left">
              <span class="wl-result-symbol">{{ getDisplaySymbol(item) }}</span>
              <span class="wl-result-name">{{ getDisplayName(item) }}</span>
            </div>
            <span class="wl-result-badge" :class="getBadgeClass(item)">{{ getSegmentLabel(item) }}</span>
          </div>
        </div>
        <div v-else-if="searchQuery.length >= 1 && !searchLoading" class="wl-search-empty">
          No symbols found
        </div>
        <div v-if="searchLoading" class="wl-search-loading">
          <span class="wl-spinner"></span> Searching...
        </div>
      </div>
    </transition>

    <!-- Watchlist Items -->
    <div class="watchlist-body" ref="watchlistBody">
      <!-- Empty state -->
      <div v-if="watchlist.length === 0" class="watchlist-empty">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
        <p class="wl-empty-title">No symbols yet</p>
        <p class="wl-empty-sub">Click the search icon above to add symbols to your watchlist</p>
      </div>

      <!-- Symbol rows -->
      <div
        v-for="(entry, idx) in watchlist"
        :key="entry.key"
        class="watchlist-item"
        :class="{ active: isCurrentSymbol(entry) }"
        @click="openChart(entry)"
      >
        <div class="wl-item-left">
          <span class="wl-item-symbol">{{ entry.symbol }}</span>
          <span class="wl-item-name">{{ entry.name }}</span>
        </div>
        <div class="wl-item-right">
          <span class="wl-item-badge" :class="getBadgeClassFromEntry(entry)">{{ entry.segment }}</span>
          <button class="wl-remove-btn" @click.stop="removeSymbol(idx)" title="Remove">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Footer count -->
    <div v-if="watchlist.length > 0" class="watchlist-footer">
      {{ watchlist.length }} symbol{{ watchlist.length !== 1 ? 's' : '' }}
    </div>
  </div>
</template>

<script>
import watchlistStore from '../services/watchlistStore';

export default {
  name: 'Watchlist',
  props: {
    isDark: { type: Boolean, default: false },
    allSymbols: { type: Object, default: () => ({}) },
    currentSymbol: { type: String, default: '' },
    currentConnector: { type: String, default: '' },
    isExpanded: { type: Boolean, default: false },
  },
  data() {
    return {
      watchlist: [],
      showAddSearch: false,
      searchQuery: '',
      searchResults: [],
      searchLoading: false,
      searchTimer: null,
    };
  },
  mounted() {
    this.loadWatchlist();
    this.pruneInvalidSymbols();
  },
  watch: {
    // Re-prune when allSymbols finishes loading (they load async)
    allSymbols(newVal) {
      if (newVal && Object.keys(newVal).length > 0) {
        this.pruneInvalidSymbols();
      }
    },
  },
  methods: {
    // ── Persistence ──────────────────────────────────────────────────────────
    async loadWatchlist() {
      this.watchlist = await watchlistStore.load();
    },
    saveWatchlist() {
      // localStorage fallback is handled by watchlistStore directly now
    },

    // ── Prune invalid symbols ─────────────────────────────────────────────────
    async pruneInvalidSymbols() {
      if (!this.allSymbols || Object.keys(this.allSymbols).length === 0) return;

      const before = this.watchlist.length;
      this.watchlist = this.watchlist.filter(entry => this.isValidEntry(entry));
      if (this.watchlist.length !== before) {
        // We need to re-save if changed
        // This is tricky now with async API, but since this is just pruning local cache,
        // it might be okay.
      }
    },
    isValidEntry(entry) {
      // Binance symbols are always valid (loaded from static JSON)
      if (entry.connector === 'Binance') return true;
      // International symbols are always valid
      if (entry.connector === 'Int') return true;

      // For broker-specific symbols, check if the symbol exists in allSymbols
      const sym = (entry.symbol || '').toUpperCase();
      return Object.values(this.allSymbols).some(item => {
        const s = (item.trading_symbol || item.symbol || '').toUpperCase();
        return s === sym;
      });
    },

    // ── Search ────────────────────────────────────────────────────────────────
    onSearchInput() {
      this.searchQuery = this.searchQuery.toUpperCase();
      if (this.searchTimer) clearTimeout(this.searchTimer);
      if (!this.searchQuery) {
        this.searchResults = [];
        return;
      }
      this.searchTimer = setTimeout(() => this.performSearch(), 200);
    },
    performSearch() {
      const q = this.searchQuery.toUpperCase();
      if (!q) { this.searchResults = []; return; }

      const results = [];
      const seen = new Set();

      Object.values(this.allSymbols).forEach(item => {
        const sym = (item.trading_symbol || item.symbol || '').toUpperCase();
        const name = (item.formatted_ins_name || item.name || item.baseAsset || '').toUpperCase();
        if ((sym.includes(q) || name.includes(q)) && !seen.has(sym)) {
          seen.add(sym);
          results.push(item);
        }
      });

      // Sort: exact starts-with first
      results.sort((a, b) => {
        const as = (a.trading_symbol || a.symbol || '').toUpperCase();
        const bs = (b.trading_symbol || b.symbol || '').toUpperCase();
        const aS = as.startsWith(q) ? 0 : 1;
        const bS = bs.startsWith(q) ? 0 : 1;
        if (aS !== bS) return aS - bS;
        return as.localeCompare(bs);
      });

      this.searchResults = results.slice(0, 20);
    },
    clearSearch() {
      this.searchQuery = '';
      this.searchResults = [];
    },
    closeSearch() {
      this.showAddSearch = false;
      this.clearSearch();
    },
    addFirstResult() {
      if (this.searchResults.length > 0) this.addSymbol(this.searchResults[0]);
    },

    // ── Add / Remove ──────────────────────────────────────────────────────────
    async addSymbol(item) {
      await watchlistStore.addItem(item);
      await this.loadWatchlist();
      this.closeSearch();
    },
    async removeSymbol(idx) {
      const item = this.watchlist[idx];
      await watchlistStore.removeItem(item);
      await this.loadWatchlist();
    },

    // ── Build entry from a symbol item ────────────────────────────────────────
    buildEntry(item) {
      return watchlistStore.buildEntry(item);
    },

    // ── Open chart ────────────────────────────────────────────────────────────
    openChart(entry) {
      this.$emit('select', entry._item || entry);
    },

    // ── Helpers ───────────────────────────────────────────────────────────────
    isCurrentSymbol(entry) {
      return entry.symbol === this.currentSymbol && entry.connector === this.currentConnector;
    },
    getDisplaySymbol(item) {
      return item.trading_symbol || item.symbol || '';
    },
    getDisplayName(item) {
      return item.formatted_ins_name || item.name || item.baseAsset || '';
    },
    getSegmentLabel(item) {
      if (item.type === 'Crypto Spot') return 'Spot';
      if (item.type === 'Crypto Futures') return 'Futures';
      if (item.type === 'Fyers' || item.exchange === 'Fyers') return item.exch || 'NSE';
      if (item.type === 'Upstox' || item.exchange === 'Upstox') return item.exch || 'NSE';
      if (item.type === 'IndianMarket') return item.exch || 'NSE';
      if (item.exchange_segment) return item.exchange_segment;
      if (item.exchange) return item.exchange;
      return 'Other';
    },
    getBadgeClass(item) {
      const t = item.type || '';
      if (t === 'Crypto Spot') return 'badge-spot';
      if (t === 'Crypto Futures') return 'badge-futures';
      if (t === 'Fyers' || item.exchange === 'Fyers') return 'badge-indian';
      if (t === 'Upstox' || item.exchange === 'Upstox') return 'badge-indian';
      if (t === 'IndianMarket') return 'badge-indian';
      return 'badge-int';
    },
    getBadgeClassFromEntry(entry) {
      if (entry.connector === 'Binance') {
        return entry.category === 'Crypto Futures' ? 'badge-futures' : 'badge-spot';
      }
      if (entry.connector === 'Indian' || entry.connector === 'Fyers' || entry.connector === 'Upstox') {
        return 'badge-indian';
      }
      return 'badge-int';
    },
  },
};
</script>

<style scoped>
/* ── Container ─────────────────────────────────────────────────────────────── */
.watchlist-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  color: #111111;
  font-family: inherit;
  overflow: hidden;
}
.watchlist-container.dark {
  background: rgb(18, 24, 38);
  color: #eef1f6;
}

/* ── Header ─────────────────────────────────────────────────────────────────── */
.watchlist-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 10px;
  border-bottom: 1px solid #e0e3eb;
  flex-shrink: 0;
}
.watchlist-container.dark .watchlist-header {
  border-bottom-color: rgba(55, 59, 75, 0.8);
}
.watchlist-title {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  font-weight: 600;
  color: #111111;
}
.watchlist-container.dark .watchlist-title {
  color: #eef1f6;
}
.watchlist-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.watchlist-icon-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.watchlist-icon-btn:hover {
  background: rgba(249, 115, 22, 0.12);
  color: #f97316;
}
.watchlist-container.dark .watchlist-icon-btn {
  color: #9ca3af;
}
.watchlist-container.dark .watchlist-icon-btn:hover {
  background: rgba(249, 115, 22, 0.18);
  color: #f97316;
}

/* ── Search bar ─────────────────────────────────────────────────────────────── */
.watchlist-search-bar {
  padding: 10px 12px;
  border-bottom: 1px solid #e0e3eb;
  flex-shrink: 0;
  position: relative;
}
.watchlist-container.dark .watchlist-search-bar {
  border-bottom-color: rgba(55, 59, 75, 0.8);
}
.watchlist-search-input-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f3f4f6;
  border: 1px solid #e0e3eb;
  border-radius: 6px;
  padding: 6px 8px;
}
.watchlist-container.dark .watchlist-search-input-wrap {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(55, 59, 75, 0.8);
}
.wl-search-icon {
  color: #9ca3af;
  flex-shrink: 0;
}
.watchlist-search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 13px;
  color: #111111;
  min-width: 0;
}
.watchlist-container.dark .watchlist-search-input {
  color: #eef1f6;
}
.watchlist-search-input::placeholder {
  color: #9ca3af;
}
.wl-clear-btn {
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
}
.wl-clear-btn:hover { color: #f97316; }

/* Search results dropdown */
.watchlist-search-results {
  margin-top: 6px;
  background: #ffffff;
  border: 1px solid #e0e3eb;
  border-radius: 6px;
  overflow: hidden;
  max-height: 240px;
  overflow-y: auto;
}
.watchlist-container.dark .watchlist-search-results {
  background: #1a2035;
  border-color: rgba(55, 59, 75, 0.8);
}
.wl-search-result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  cursor: pointer;
  transition: background 0.12s;
  gap: 8px;
}
.wl-search-result-item:hover {
  background: rgba(249, 115, 22, 0.08);
}
.watchlist-container.dark .wl-search-result-item:hover {
  background: rgba(249, 115, 22, 0.12);
}
.wl-result-left {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.wl-result-symbol {
  font-size: 13px;
  font-weight: 600;
  color: #111111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.watchlist-container.dark .wl-result-symbol { color: #eef1f6; }
.wl-result-name {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}
.watchlist-container.dark .wl-result-name { color: #9ca3af; }
.wl-search-empty {
  padding: 12px;
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
}
.wl-search-loading {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  font-size: 12px;
  color: #9ca3af;
}
.wl-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid #e0e3eb;
  border-top-color: #f97316;
  border-radius: 50%;
  animation: wl-spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes wl-spin { to { transform: rotate(360deg); } }

/* ── Watchlist body ─────────────────────────────────────────────────────────── */
.watchlist-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}
.watchlist-body::-webkit-scrollbar { width: 4px; }
.watchlist-body::-webkit-scrollbar-track { background: transparent; }
.watchlist-body::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 2px; }
.watchlist-container.dark .watchlist-body::-webkit-scrollbar-thumb { background: #374151; }

/* Empty state */
.watchlist-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 10px;
  color: #9ca3af;
  text-align: center;
}
.wl-empty-title {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin: 0;
}
.watchlist-container.dark .wl-empty-title { color: #9ca3af; }
.wl-empty-sub {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
  line-height: 1.5;
}

/* Symbol row */
.watchlist-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  cursor: pointer;
  border-bottom: 1px solid transparent;
  transition: background 0.12s;
  gap: 8px;
}
.watchlist-item:hover {
  background: rgba(249, 115, 22, 0.07);
}
.watchlist-item.active {
  background: rgba(249, 115, 22, 0.12);
  border-left: 3px solid #f97316;
  padding-left: 9px;
}
.watchlist-container.dark .watchlist-item:hover {
  background: rgba(249, 115, 22, 0.1);
}
.watchlist-container.dark .watchlist-item.active {
  background: rgba(249, 115, 22, 0.15);
}
.wl-item-left {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.wl-item-symbol {
  font-size: 13px;
  font-weight: 600;
  color: #111111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.watchlist-container.dark .wl-item-symbol { color: #eef1f6; }
.wl-item-name {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.watchlist-container.dark .wl-item-name { color: #9ca3af; }
.wl-item-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.wl-remove-btn {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  opacity: 1;
  transition: background 0.15s, color 0.15s;
  padding: 0;
}
.wl-remove-btn:hover {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

/* Badges */
.wl-result-badge,
.wl-item-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.badge-spot {
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
}
.badge-futures {
  background: rgba(139, 92, 246, 0.12);
  color: #8b5cf6;
}
.badge-indian {
  background: rgba(249, 115, 22, 0.12);
  color: #f97316;
}
.badge-int {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
}
.watchlist-container.dark .badge-spot { background: rgba(16, 185, 129, 0.18); }
.watchlist-container.dark .badge-futures { background: rgba(139, 92, 246, 0.18); }
.watchlist-container.dark .badge-indian { background: rgba(249, 115, 22, 0.18); }
.watchlist-container.dark .badge-int { background: rgba(59, 130, 246, 0.18); }

/* ── Footer ─────────────────────────────────────────────────────────────────── */
.watchlist-footer {
  padding: 8px 14px;
  font-size: 11px;
  color: #9ca3af;
  border-top: 1px solid #e0e3eb;
  flex-shrink: 0;
}
.watchlist-container.dark .watchlist-footer {
  border-top-color: rgba(55, 59, 75, 0.8);
}

/* ── Transition ─────────────────────────────────────────────────────────────── */
.wl-fade-enter-active,
.wl-fade-leave-active { transition: opacity 0.15s, transform 0.15s; }
.wl-fade-enter, .wl-fade-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
